import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronRight, ExternalLink, Loader2, Search, Send, Minimize2, X, ArrowLeft } from 'lucide-react';
import { getSinaHelp } from './sinaHelpContent.js';
import { getSinaState, SINA_IDLE_FRAMES } from './sinaStates.js';
import SinaTruckLoader from './SinaTruckLoader.jsx';
import { preloadSinaTruckFrames, SINA_TRUCK_EXIT_MS as TRUCK_EXIT_MS } from './sinaTruck.js';
import { getSinaBusyCount, subscribeToSinaBusy } from './sinaBusy.js';
import { readSinaHidden, SINA_VISIBILITY_EVENT } from './sinaPreferences.js';
import { fetchGuideDetail, fetchGuideOperationalContext, fetchGuides } from '@/services/guides.js';
import { buildGuiaOperationalDiagnosis } from './guideDiagnosis.js';
import { getFojaDetail, listFojas } from '@/services/fojas.js';
import { fetchReparto, fetchRepartos } from '@/services/repartos.js';
import { searchClients } from '@/services/clients.js';
import { useAuth } from '@/state/auth/AuthContext.jsx';
import { formatNumeroGuia } from '@/lib/guias/validation.js';
import './SinaAssistant.css';
import SinaNotificationHost from './SinaNotificationHost.jsx';
import { sina } from './sinaNotifications.js';
import { buildPendingHighlights, buildSinaGreeting, greetingStamp, shouldGreet } from './sinaGreeting.js';
import { fetchDashboardCargas } from '@/services/dashboards.js';
import { getPermissionCodesForPath } from '@/security/access.js';
import {
  alertCounts,
  buildFojaDiagnosis,
  buildOperationalAlerts,
  buildPendingSummary,
  buildProactiveNotice,
  canSeePendingWork,
  findNewAlerts,
  isSummaryQuery,
  selectUrgentAlerts,
} from './sinaInsights.js';
import {
  BLINK_DURATION_MS,
  canBlink,
  canPlayIdleGesture,
  GESTURE_DURATION_MS,
  nextBlinkDelay,
  nextGestureDelay,
  pickIdleGesture,
  TYPING_PAUSE_MS,
} from './sinaIdle.js';

const storageKeys = {
  alertBaseline: 'sinamon.sina.alertBaseline',
  dailyGreeting: 'sinamon.sina.dailyGreeting',
  panelOpen: 'sinamon.sina.panelOpen',
  minimized: 'sinamon.sina.minimized',
};

const SINA_VIEW = Object.freeze({
  HOME: 'home',
  RESULT: 'result',
  ERROR: 'error',
  LOADING: 'loading',
  DIAGNOSIS: 'diagnosis',
  SUMMARY: 'summary',
});

const GREETING_DELAY_MS = 1200;
const GREETING_DATA_TIMEOUT_MS = 3000;
const GREETING_READ_MS = 6000;
const GREETING_CHAR_MS = 24;
const ALERT_WATCH_FIRST_MS = 15_000;
const ALERT_WATCH_INTERVAL_MS = 5 * 60_000;
const ALERT_NOTICE_READ_MS = 9000;

function readText(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function storeText(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Sin storage el saludo puede repetirse; no bloquea nada.
  }
}

const canOpenRoute = (auth, route) => getPermissionCodesForPath(String(route).split('?')[0]).some((code) => auth.can(code));

const activeSucursalId = (auth) => {
  const sucursal = auth?.sucursalActiva || auth?.sucursal || null;
  return sucursal?.id || null;
};

const canSeePending = (auth) => canSeePendingWork((route) => canOpenRoute(auth, route));

// Los pendientes salen de los datos del tablero de cargas, solo si el usuario puede abrir
// alguna pantalla de pendientes y sin esperar más de unos segundos. Devuelve null si no hay datos.
async function loadCargasSnapshot(auth) {
  if (!canSeePending(auth)) return null;
  const timeout = new Promise((resolve) => window.setTimeout(() => resolve(null), GREETING_DATA_TIMEOUT_MS));
  try {
    return await Promise.race([fetchDashboardCargas({ sucursalId: activeSucursalId(auth) }), timeout]);
  } catch {
    return null;
  }
}

// La base se comparte entre pestañas para que un mismo cambio avise una sola vez,
// y se renueva por día y sucursal porque el saludo ya cuenta lo heredado.
function readAlertBaseline(scope) {
  try {
    const stored = JSON.parse(readText(storageKeys.alertBaseline) || 'null');
    return stored?.scope === scope ? stored.counts : null;
  } catch {
    return null;
  }
}

function readPreference(key, fallback = false) {
  try {
    return window.localStorage.getItem(key) === 'true';
  } catch {
    return fallback;
  }
}

function storePreference(key, value) {
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    // Las preferencias visuales son opcionales.
  }
}

const normalizeText = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase();

function parseOperationalQuery(value) {
  const text = normalizeText(value).trim();
  const diagnosisIntent = /(que\s+le\s+falta|analiz|por\s+que\s+no\s+(se\s+puede|sale|llega|avanza)|que\s+pasa\s+con|que\s+puedo\s+hacer|sigue\s+pendiente|estado\s+de\s+la\s+(guia|foja))/.test(text);
  const documentNumber = text.match(/(?:guia\s*)?(\d{1,4})\s*-\s*(\d+)\b/);
  if (documentNumber) {
    return { id: String(Number(documentNumber[2])), type: 'guia', intent: diagnosisIntent ? 'GUIDE_DIAGNOSIS' : 'GUIDE_LOOKUP' };
  }
  const clientSearch = text.match(/^(?:cliente|client)\s*[:#-]?\s*(.+)$/);
  if (clientSearch?.[1]?.trim()) {
    return { search: clientSearch[1].trim().replace(/^['"]|['"]$/g, ''), type: 'cliente' };
  }
  const match = text.match(/(?:guia|foja(?:\s+de\s+ruta)?|reparto)?\s*(?:n(?:ro|umero)?\.?\s*)?#?\s*(\d+)\b/);
  if (!match) return null;
  return { id: match[1], type: /foja/.test(text) ? 'foja' : /reparto/.test(text) ? 'reparto' : 'guia', intent: diagnosisIntent ? 'GUIDE_DIAGNOSIS' : 'GUIDE_LOOKUP' };
}

const present = (value, fallback = 'Sin dato') => value === undefined || value === null || value === '' ? fallback : value;
const titleCase = (value) => String(value || 'Sin estado').replaceAll('_', ' ').toLowerCase().replace(/(^|\s)\S/g, (letter) => letter.toUpperCase());
const pickFirst = (...values) => values.find((value) => value !== undefined && value !== null && String(value).trim() !== '');

function getGuiaStatusMessage(status) {
  const normalized = String(status || '').trim().toUpperCase();
  const messages = {
    EMITIDA: 'La guía fue emitida y todavía no ingresó a una foja.',
    EN_FOJA: 'La guía ya fue incorporada a una foja de ruta.',
    FOJA_CERRADA: 'La guía ya fue incorporada a una foja de ruta.',
    EN_TRANSITO: 'La guía se encuentra en tránsito hacia su destino.',
    EN_TRANSITO_A_DESTINO: 'La guía se encuentra en tránsito hacia su destino.',
    EN_TRANSITO_A_VIA: 'La guía se encuentra en tránsito hacia su destino.',
    RECIBIDA_DESTINO: 'La guía ya fue recibida en la sucursal de destino.',
    EN_REPARTO: 'La guía está asignada a un reparto.',
    ENTREGADA: 'La guía fue entregada correctamente.',
    RETENIDA: 'La guía registra una retención y requiere revisión.',
  };
  return messages[normalized] || 'Encontré la guía solicitada. Consultá el detalle para ver su estado operativo actualizado.';
}
const getFojaNumber = (foja) => Number(foja?.numero ?? foja?.id);

function findClosestFoja(fojas, requestedNumber) {
  const requested = Number(requestedNumber);
  if (!Number.isFinite(requested)) return null;

  return fojas.reduce((closest, foja) => {
    const number = getFojaNumber(foja);
    if (!Number.isFinite(number)) return closest;
    if (!closest || Math.abs(number - requested) < Math.abs(getFojaNumber(closest) - requested)) return foja;
    return closest;
  }, null);
}

export default function SinaAssistant() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();
  const rootRef = useRef(null);
  const searchInputRef = useRef(null);
  const [isOpen, setIsOpen] = useState(() => readPreference(storageKeys.panelOpen));
  const [isMinimized, setIsMinimized] = useState(() => readPreference(storageKeys.minimized));
  const [isHidden, setIsHidden] = useState(() => readSinaHidden());
  const [speakingState, setSpeakingState] = useState(null);
  const [activeActionId, setActiveActionId] = useState(null);
  const [query, setQuery] = useState('');
  const [queryResult, setQueryResult] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [view, setView] = useState(SINA_VIEW.HOME);
  const [hasBlockingOverlay, setHasBlockingOverlay] = useState(false);
  const [urgentAlerts, setUrgentAlerts] = useState([]);
  const [gesture, setGesture] = useState(null);
  const [blinking, setBlinking] = useState(false);
  const [showTruck, setShowTruck] = useState(false);
  const [truckLeaving, setTruckLeaving] = useState(false);
  const globalBusy = useSyncExternalStore(subscribeToSinaBusy, getSinaBusyCount) > 0;
  const isWorking = globalBusy || view === SINA_VIEW.LOADING;
  const help = useMemo(() => getSinaHelp(pathname), [pathname]);
  const sinaState = getSinaState(view === SINA_VIEW.LOADING ? 'thinking' : view === SINA_VIEW.ERROR ? 'error' : view === SINA_VIEW.DIAGNOSIS ? queryResult?.diagnosis?.sinaState || 'help' : view === SINA_VIEW.SUMMARY ? queryResult?.sinaState || 'help' : view === SINA_VIEW.RESULT ? 'success' : activeActionId ? 'help' : 'idle');

  useEffect(() => { preloadSinaTruckFrames(); }, []);

  // Pequeño delay: las consultas instantáneas no deben hacer parpadear el camión.
  useEffect(() => {
    if (isWorking) {
      setTruckLeaving(false);
      if (showTruck) return undefined;
      const timer = window.setTimeout(() => setShowTruck(true), 300);
      return () => window.clearTimeout(timer);
    }
    if (!showTruck) return undefined;
    // Salida suave: el camión se va antes de que vuelva la cara en reposo.
    setTruckLeaving(true);
    const timer = window.setTimeout(() => { setShowTruck(false); setTruckLeaving(false); }, TRUCK_EXIT_MS);
    return () => window.clearTimeout(timer);
  }, [isWorking, showTruck]);

  const userKey = auth?.user?.id != null ? String(auth.user.id) : null;
  useEffect(() => {
    if (!userKey || isHidden || !shouldGreet(readText(storageKeys.dailyGreeting), userKey)) return undefined;
    let cancelled = false;
    const timeout = window.setTimeout(async () => {
      storeText(storageKeys.dailyGreeting, greetingStamp(userKey));
      const highlights = buildPendingHighlights(await loadCargasSnapshot(auth));
      if (cancelled) return;
      const { title, message } = buildSinaGreeting({ user: auth.user, highlights });
      sina.notify({
        type: 'info',
        avatarState: highlights.length ? 'help' : 'success',
        eyebrow: 'SINA · Hola',
        title,
        message,
        typewriter: true,
        duration: GREETING_READ_MS + message.length * GREETING_CHAR_MS,
      });
    }, GREETING_DELAY_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
    // El saludo depende solo de quién inició sesión; auth cambia de identidad en cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userKey, isHidden]);

  // Sina revisa el tablero cada tanto y avisa solo cuando aparece algo urgente nuevo.
  const authRef = useRef(auth);
  authRef.current = auth;
  const sucursalKey = activeSucursalId(auth);
  useEffect(() => {
    if (!userKey || isHidden) return undefined;
    let cancelled = false;
    const check = async () => {
      if (document.visibilityState !== 'visible') return;
      const currentAuth = authRef.current;
      const dashboard = await loadCargasSnapshot(currentAuth);
      if (cancelled || !dashboard) return;
      const scope = `${greetingStamp(userKey)}:${sucursalKey ?? 'todas'}`;
      const alerts = buildOperationalAlerts(dashboard);
      setUrgentAlerts(selectUrgentAlerts(alerts, (route) => canOpenRoute(currentAuth, route)));
      const notice = buildProactiveNotice(findNewAlerts(readAlertBaseline(scope), alerts));
      storeText(storageKeys.alertBaseline, JSON.stringify({ scope, counts: alertCounts(alerts) }));
      if (!notice) return;
      sina.notify({
        type: 'warning',
        avatarState: 'warning',
        eyebrow: 'SINA · Atención',
        title: notice.title,
        message: notice.message,
        typewriter: true,
        duration: ALERT_NOTICE_READ_MS + notice.message.length * GREETING_CHAR_MS,
        primaryAction: canOpenRoute(currentAuth, notice.to) ? { label: 'Ver ahora', onClick: () => navigate(notice.to) } : undefined,
      });
    };
    const firstCheck = window.setTimeout(check, ALERT_WATCH_FIRST_MS);
    const interval = window.setInterval(check, ALERT_WATCH_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(firstCheck);
      window.clearInterval(interval);
    };
  }, [userKey, isHidden, sucursalKey, navigate]);

  // Sina en reposo hace un gesto cada tanto; si hay urgentes, el gesto es el aviso.
  const idleRef = useRef({});
  idleRef.current = { isOpen, hasBlockingOverlay, hasUrgent: urgentAlerts.length > 0 };
  useEffect(() => {
    if (isHidden) return undefined;
    let lastTypingAt = 0;
    let gestureTimer;
    let resetTimer;
    const markTyping = () => { lastTypingAt = Date.now(); };
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const play = () => {
      const current = idleRef.current;
      const allowed = canPlayIdleGesture({
        ...current,
        documentHidden: document.visibilityState !== 'visible',
        reducedMotion: Boolean(reducedMotion?.matches),
        typing: Date.now() - lastTypingAt < TYPING_PAUSE_MS,
      });
      if (allowed) {
        setGesture(pickIdleGesture({ hasUrgent: current.hasUrgent }));
        resetTimer = window.setTimeout(() => setGesture(null), GESTURE_DURATION_MS);
      }
      gestureTimer = window.setTimeout(play, nextGestureDelay());
    };
    document.addEventListener('keydown', markTyping, true);
    gestureTimer = window.setTimeout(play, nextGestureDelay());
    return () => {
      document.removeEventListener('keydown', markTyping, true);
      window.clearTimeout(gestureTimer);
      window.clearTimeout(resetTimer);
      setGesture(null);
    };
  }, [isHidden]);

  // Parpadeo independiente de los gestos: corto, frecuente y solo con la cara en reposo.
  const blinkRef = useRef({});
  blinkRef.current = { hasUrgent: urgentAlerts.length > 0, gesture };
  useEffect(() => {
    if (isHidden) return undefined;
    let blinkTimer;
    let openTimer;
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const blink = () => {
      const allowed = canBlink({
        ...blinkRef.current,
        documentHidden: document.visibilityState !== 'visible',
        reducedMotion: Boolean(reducedMotion?.matches),
      });
      if (allowed) {
        setBlinking(true);
        openTimer = window.setTimeout(() => setBlinking(false), BLINK_DURATION_MS);
      }
      blinkTimer = window.setTimeout(blink, nextBlinkDelay());
    };
    blinkTimer = window.setTimeout(blink, nextBlinkDelay());
    return () => {
      window.clearTimeout(blinkTimer);
      window.clearTimeout(openTimer);
      setBlinking(false);
    };
  }, [isHidden]);

  useEffect(() => {
    storePreference(storageKeys.panelOpen, isOpen);
  }, [isOpen]);

  useEffect(() => {
    storePreference(storageKeys.minimized, isMinimized);
  }, [isMinimized]);

  useEffect(() => {
    const syncVisibility = () => setIsHidden(readSinaHidden());
    window.addEventListener(SINA_VISIBILITY_EVENT, syncVisibility);
    return () => window.removeEventListener(SINA_VISIBILITY_EVENT, syncVisibility);
  }, []);

  useEffect(() => {
    setActiveActionId(null);
    setQueryResult(null);
    setView(SINA_VIEW.HOME);
  }, [pathname]);

  useEffect(() => {
    const checkForOverlay = () => {
      const dialogs = [...document.querySelectorAll('[role="dialog"][aria-modal="true"]')];
      setHasBlockingOverlay(dialogs.some((dialog) => {
        if (rootRef.current?.contains(dialog)) return false;
        const rect = dialog.getBoundingClientRect();
        const style = window.getComputedStyle(dialog);
        return style.visibility !== 'hidden'
          && style.display !== 'none'
          && rect.right > 0
          && rect.left < window.innerWidth
          && rect.bottom > 0
          && rect.top < window.innerHeight;
      }));
    };

    checkForOverlay();
    const observer = new MutationObserver(checkForOverlay);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-modal', 'role'] });
    return () => observer.disconnect();
  }, []);

  const closePanel = () => {
    setIsOpen(false);
    setActiveActionId(null);
  };

  const togglePanel = () => {
    setIsMinimized(false);
    setGesture(null);
    // Si Sina está avisando algo urgente, abrir ya muestra los pendientes.
    if (!isOpen && urgentAlerts.length && view === SINA_VIEW.HOME) showPendingSummary();
    setIsOpen((open) => !open);
  };

  const minimize = () => {
    setIsMinimized(true);
    closePanel();
  };

  const clearSearch = () => {
    setQuery('');
    setQueryResult(null);
    setActiveActionId(null);
    setView(SINA_VIEW.HOME);
    window.requestAnimationFrame(() => searchInputRef.current?.focus());
  };

  const analyzeGuide = async () => {
    if (!queryResult?.guideId || !queryResult?.guide) return;
    setIsSearching(true);
    setView(SINA_VIEW.LOADING);
    try {
      const context = await fetchGuideOperationalContext(queryResult.guideId);
      const diagnosis = buildGuiaOperationalDiagnosis(queryResult.guide, context, auth.can);
      setQueryResult((current) => ({ ...current, context, diagnosis }));
      setView(SINA_VIEW.DIAGNOSIS);
    } catch (error) {
      setQueryResult({ error: error.message || 'No pude analizar la situación de esta guía.' });
      setView(SINA_VIEW.ERROR);
    } finally {
      setIsSearching(false);
    }
  };

  const showPendingSummary = async () => {
    if (!canSeePending(auth)) {
      setQueryResult({ error: 'No tenés acceso a ninguna pantalla de pendientes, así que no puedo armarte el resumen.' });
      setView(SINA_VIEW.ERROR);
      return;
    }
    setIsSearching(true);
    setActiveActionId(null);
    setQueryResult(null);
    setView(SINA_VIEW.LOADING);
    const dashboard = await loadCargasSnapshot(auth);
    setIsSearching(false);
    if (!dashboard) {
      setQueryResult({ error: 'No pude leer el tablero de cargas. Probá de nuevo en un rato.' });
      setView(SINA_VIEW.ERROR);
      return;
    }
    setQueryResult(buildPendingSummary(buildOperationalAlerts(dashboard).filter((alert) => canOpenRoute(auth, alert.to))));
    setView(SINA_VIEW.SUMMARY);
  };

  const searchOperationalRecord = async (event) => {
    event.preventDefault();
    if (isSummaryQuery(query)) {
      await showPendingSummary();
      return;
    }
    const parsed = parseOperationalQuery(query);
    if (!parsed) {
      setQueryResult({ error: 'Indicá una guía, foja, reparto o cliente para poder buscarlo.', examples: true });
      setView(SINA_VIEW.ERROR);
      return;
    }

    setIsSearching(true);
    setActiveActionId(null);
    setQueryResult(null);
    setView(SINA_VIEW.LOADING);
    try {
      if (parsed.type === 'cliente') {
        const clientListing = await searchClients({ search: parsed.search, page: 1, pageSize: 10 });
        const clients = clientListing.data || [];
        const normalizedSearch = normalizeText(parsed.search);
        const cuitSearch = parsed.search.replace(/\D/g, '');
        const client = clients.find((item) => normalizeText(item.razonSocial) === normalizedSearch
          || (cuitSearch && String(item.cuit || '').replace(/\D/g, '') === cuitSearch)) || clients[0];

        if (!client?.id) {
          setQueryResult({
            error: `No encontré clientes que coincidan con "${parsed.search}".`,
            suggestion: { text: 'Abrir el listado para revisar o dar de alta un cliente.', route: '/clientes' },
          });
          setView(SINA_VIEW.ERROR);
          return;
        }

        setQueryResult({
          title: `Cliente: ${present(client.razonSocial)}`,
          route: '/clientes',
          action: 'Abrir listado de clientes',
          lines: [
            ['CUIT', present(client.cuit)],
            ['Teléfono', present(client.telefono || client.celular)],
            ['Localidad', present(client.localidad?.nombre)],
            ['Coincidencias', String(clientListing.total || clients.length)],
          ],
        });
        setView(SINA_VIEW.RESULT);
        return;
      }

      if (parsed.type === 'guia') {
        const listing = await fetchGuides({ filters: { guideNumber: parsed.id }, page: 1, limit: 5 });
        const guides = listing.data || [];
        const guide = guides.find((item) => String(item.nro_guia || item.numero_guia) === parsed.id) || guides[0];
        if (!guide?.id) {
          setQueryResult({ error: 'No encontré una guía con ese número.', examples: true });
          setView(SINA_VIEW.ERROR);
          return;
        }
        const detailPayload = await fetchGuideDetail(guide.id);
        const detail = detailPayload.data || detailPayload.guia || detailPayload;
        const estado = pickFirst(detail.estado_logistico, detail.estado, guide.estado_logistico, guide.estado);
        const puntoVenta = pickFirst(detail.puntoVentaNumero, detail.punto_venta_numero, guide.punto_venta_numero, guide.puntoVentaNumero);
        const numero = pickFirst(detail.numero, detail.numero_guia, guide.nro_guia, guide.numero_guia, parsed.id);
        const guideResult = {
          type: 'guia',
          title: `Guía ${formatNumeroGuia(puntoVenta, numero) || present(numero)}`,
          route: `/guias/${guide.id}`,
          action: 'Abrir detalle de la guía',
          status: titleCase(estado),
          message: getGuiaStatusMessage(estado),
          guideId: guide.id,
          guide: detail,
          sender: present(pickFirst(detail.remitente_nombre, detail.remitente?.nombre, detail.remitente, guide.remitente, guide.cliente)),
          receiver: present(pickFirst(detail.destinatario_nombre, detail.destinatario?.nombre, detail.destinatario, guide.destinatario)),
          origin: present(pickFirst(detail.sucursalOrigen?.nombre, detail.sucursal_origen?.nombre, detail.origen, guide.origen, guide.sucursal_origen)),
          destination: present(pickFirst(detail.sucursalDestino?.nombre, detail.sucursal_destino?.nombre, detail.destino, guide.destino, guide.sucursal_destino, detail.localidad_destino_nombre, guide.localidad_destino)),
        };
        if (parsed.intent === 'GUIDE_DIAGNOSIS') {
          const context = await fetchGuideOperationalContext(guide.id);
          guideResult.context = context;
          guideResult.diagnosis = buildGuiaOperationalDiagnosis(detail, context, auth.can);
          setQueryResult(guideResult);
          setView(SINA_VIEW.DIAGNOSIS);
          return;
        }
        setQueryResult(guideResult);
        setView(SINA_VIEW.RESULT);
        return;
      }

      if (parsed.type === 'foja') {
        const sucursal = auth?.sucursalActiva || auth?.sucursal || null;
        const sucursalId = sucursal?.id || sucursal?.sucursal_id || null;
        const sucursalNombre = sucursal?.nombre || sucursal?.descripcion || 'la sucursal activa';
        const fojas = await listFojas({ sucursal_id: sucursalId, limit: 500 });
        const matchedFoja = fojas.find((item) => String(item.numero) === parsed.id || String(item.id) === parsed.id);

        if (!matchedFoja?.id) {
          const closestFoja = findClosestFoja(fojas, parsed.id);
          setQueryResult({
            error: `No existe la foja #${parsed.id} en ${sucursalNombre}.`,
            suggestion: closestFoja ? {
              text: `La foja más cercana es la #${present(closestFoja.numero || closestFoja.id)}.`,
              route: `/cargas/fojas-de-ruta?id=${closestFoja.id}`,
            } : null,
          });
          setView(SINA_VIEW.ERROR);
          return;
        }

        const detail = await getFojaDetail(matchedFoja.id);
        const foja = detail.foja || detail;
        if (!foja?.id) throw new Error(`No encontré la foja ${parsed.id}.`);
        setQueryResult({
          type: 'foja',
          title: `Foja #${present(foja.numero || foja.id)}`,
          route: `/cargas/fojas-de-ruta?id=${foja.id}`,
          action: 'Abrir foja de ruta',
          lines: [['Estado', titleCase(foja.estado)], ['Línea', present(foja.linea)], ['Origen', present(foja.sucursal_origen_nombre || foja.sucursal_origen_id)], ['Destino', present(foja.sucursal_destino_nombre || foja.sucursal_destino_id)], ['Guías', String((detail.guias || []).length)]],
          diagnosis: buildFojaDiagnosis(detail, { canOpen: (route) => canOpenRoute(auth, route) }),
        });
        setView(parsed.intent === 'GUIDE_DIAGNOSIS' ? SINA_VIEW.DIAGNOSIS : SINA_VIEW.RESULT);
        return;
      }

      const listingResponse = await fetchRepartos();
      const repartos = listingResponse.data?.data || listingResponse.data || [];
      const reparto = repartos.find((item) => String(item.numero) === parsed.id || String(item.id) === parsed.id);
      if (!reparto?.id) throw new Error(`No encontré el reparto ${parsed.id}.`);
      const detailResponse = await fetchReparto(reparto.id);
      const detail = detailResponse.data?.data || detailResponse.data || {};
      const record = detail.reparto || reparto;
      setQueryResult({
        title: `Reparto #${present(record.numero || reparto.numero || record.id)}`,
        route: `/repartos/${record.id || reparto.id}`,
        action: 'Ver detalle del reparto',
        lines: [['Estado', titleCase(record.estado || reparto.estado)], ['Fecha', present(record.fecha || reparto.fecha)], ['Chofer', present(record.chofer_nombre || reparto.chofer_nombre)], ['Unidad', present(record.unidad_patente || reparto.unidad_patente)], ['Guías', String((detail.guias || []).length)]],
      });
      setView(SINA_VIEW.RESULT);
    } catch (error) {
      if (parsed.type === 'foja' && error?.response?.status === 404) {
        const sucursal = auth?.sucursalActiva || auth?.sucursal || null;
        const sucursalNombre = sucursal?.nombre || sucursal?.descripcion || 'la sucursal activa';
        setQueryResult({ error: `No existe la foja #${parsed.id} en ${sucursalNombre}.` });
      } else {
        setQueryResult({ error: error.message || 'No pude consultar ese registro. Intentá nuevamente.' });
      }
      setView(SINA_VIEW.ERROR);
    } finally {
      setIsSearching(false);
    }
  };

  if (isHidden) return null;

  const urgentLabel = urgentAlerts.map((alert) => alert.label).join(', ');

  return (
    <div
      ref={rootRef}
      className={`sina-assistant ${isOpen ? 'sina-assistant--open' : ''} ${isMinimized ? 'sina-assistant--minimized' : ''} ${hasBlockingOverlay ? 'sina-assistant--hidden' : ''}`}
    >
      <SinaNotificationHost onSpeakingChange={setSpeakingState} />

      {isOpen ? (
        <section className="sina-panel" role="dialog" aria-modal="false" aria-labelledby="sina-panel-title">
          <header className="sina-panel__header">
            <div className="sina-panel__identity">
              <img
                className="sina-panel__state-image"
                src={sinaState.image}
                alt={`Sina: ${sinaState.label}`}
              />
              <div>
                <p className="sina-panel__eyebrow">Asistencia operativa</p>
                <h2 id="sina-panel-title">Sina</h2>
                <p>Tu copiloto operativo</p>
              </div>
            </div>
            <div className="sina-panel__controls">
              <button type="button" onClick={minimize} aria-label="Minimizar Sina" title="Minimizar"><Minimize2 size={17} /></button>
              <button type="button" onClick={closePanel} aria-label="Cerrar panel de Sina" title="Cerrar"><X size={18} /></button>
            </div>
          </header>
          <div className="sina-panel__body">
            <form className="sina-search" onSubmit={searchOperationalRecord}>
              <label htmlFor="sina-operational-search">Consultá un registro</label>
              <div>
                <Search size={15} aria-hidden="true" />
                <input ref={searchInputRef} id="sina-operational-search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="¿Qué tengo pendiente? · foja 12 · guía 29" autoComplete="off" />
                <button type="submit" disabled={isSearching} aria-label="Consultar registro">
                  {isSearching ? <Loader2 size={16} className="sina-spin" /> : <Send size={16} />}
                </button>
              </div>
              <p>Preguntame qué tenés pendiente, "¿por qué no sale la foja 12?", un número de guía, reparto o "cliente" seguido de su nombre o CUIT.</p>
            </form>
            {view === SINA_VIEW.LOADING ? <div className="sina-query-result sina-query-result--loading" role="status">{showTruck ? <SinaTruckLoader label="Buscando el registro" leaving={truckLeaving} /> : <Loader2 size={16} className="sina-spin" />}<span>Buscando el registro...</span></div> : null}
            {view === SINA_VIEW.ERROR && queryResult ? (
              <div className={`sina-query-result ${queryResult.error ? 'sina-query-result--error' : ''}`} role="status">
                <span>{queryResult.error}</span>
                {queryResult.examples ? <span className="sina-query-result__examples">Probá con:<br />• ¿qué tengo pendiente?<br />• ¿por qué no sale la foja 12?<br />• guía 0001-000000000029<br />• reparto 8<br />• cliente Acme</span> : null}
                {queryResult.suggestion ? <button type="button" onClick={() => { navigate(queryResult.suggestion.route); closePanel(); }}><span>{queryResult.suggestion.text}</span><ExternalLink size={14} /></button> : null}
                <button type="button" className="sina-query-result__secondary" onClick={clearSearch}>Limpiar búsqueda</button>
              </div>
            ) : null}
            {view === SINA_VIEW.RESULT && queryResult ? <div className="sina-query-result sina-query-result--result" role="status">
              {queryResult.type === 'guia' ? <><div className="sina-guide-result__heading"><strong>{queryResult.title}</strong><span className="sina-guide-result__badge">{queryResult.status}</span></div><p className="sina-guide-result__message">{queryResult.message}</p><dl className="sina-guide-result__details"><div><dt>Remitente</dt><dd title={queryResult.sender}>{queryResult.sender}</dd></div><div><dt>Destinatario</dt><dd title={queryResult.receiver}>{queryResult.receiver}</dd></div><div className="sina-guide-result__route"><dt>Ruta</dt><dd title={`${queryResult.origin} → ${queryResult.destination}`}>{queryResult.origin} <span aria-hidden="true">→</span> {queryResult.destination}</dd></div></dl></> : <><strong>{queryResult.title}</strong><dl>{queryResult.lines.map(([label, value]) => <div key={label}><dt>{label}</dt><dd title={value}>{value}</dd></div>)}</dl></>}
              {queryResult.type === 'guia' ? <button type="button" onClick={analyzeGuide}><span>Analizar situación</span><ChevronRight size={14} /></button> : null}
              {queryResult.type === 'foja' && queryResult.diagnosis ? <button type="button" onClick={() => setView(SINA_VIEW.DIAGNOSIS)}><span>Analizar situación</span><ChevronRight size={14} /></button> : null}
              <button type="button" onClick={() => { navigate(queryResult.route); closePanel(); }}><span>{queryResult.action}</span><ExternalLink size={14} /></button><button type="button" className="sina-query-result__secondary" onClick={clearSearch}>← Nueva consulta</button>
            </div> : null}
            {view === SINA_VIEW.DIAGNOSIS && queryResult?.diagnosis ? <div className="sina-diagnosis" role="status">
              <button type="button" className="sina-diagnosis__back" onClick={() => setView(SINA_VIEW.RESULT)}><ArrowLeft size={14} /> Volver al resultado</button>
              <div className="sina-guide-result__heading"><strong>{queryResult.title}</strong><span className="sina-guide-result__badge">{queryResult.diagnosis.title}</span></div>
              <h3>Situación actual</h3><p>{queryResult.diagnosis.conversational}</p>
              <h3>Recorrido operativo</h3><ol className="sina-diagnosis__stages">{queryResult.diagnosis.stages.filter((item) => item.status !== 'not_applicable').map((item) => <li key={item.key} data-status={item.status}>{item.status === 'completed' ? '✓' : item.status === 'current' ? '◷' : item.status === 'blocked' ? '!' : '—'} {item.label}</li>)}</ol>
              {queryResult.diagnosis.missingSteps.length || queryResult.diagnosis.warnings.length ? <><h3>Puntos a revisar</h3><ul>{[...queryResult.diagnosis.missingSteps, ...queryResult.diagnosis.warnings].map((item) => <li key={item}>{item}</li>)}</ul></> : null}
              <h3>Próximas acciones</h3><div className="sina-diagnosis__actions">{queryResult.diagnosis.availableActions.map((item) => <button type="button" key={item.key} onClick={() => { navigate(item.route); closePanel(); }}>{item.label}<ExternalLink size={14} /></button>)}</div>
              <button type="button" className="sina-query-result__secondary" onClick={clearSearch}>Nueva consulta</button>
            </div> : null}
            {view === SINA_VIEW.SUMMARY && queryResult ? <div className="sina-diagnosis" role="status">
              <div className="sina-guide-result__heading"><strong>{queryResult.title}</strong></div>
              <p>{queryResult.message}</p>
              {queryResult.alerts.length ? <><h3>Ir a resolverlo</h3><div className="sina-diagnosis__actions">{queryResult.alerts.map((alert) => <button type="button" key={alert.id} onClick={() => { navigate(alert.to); closePanel(); }}>{alert.label}<ExternalLink size={14} /></button>)}</div></> : null}
              <button type="button" className="sina-query-result__secondary" onClick={clearSearch}>Nueva consulta</button>
            </div> : null}
            {view === SINA_VIEW.HOME ? <>
            <p className="sina-intro">Hola, soy Sina. Estoy para ayudarte a usar Sinamon y entender cada etapa de la operación.</p>
            <div className="sina-context" aria-live="polite">
              <strong>{help.title}</strong>
              <span>{help.description}</span>
            </div>
            <div className="sina-actions" aria-label="Temas de ayuda">
              {help.actions.map((action) => {
                const isExpanded = activeActionId === action.id;
                const responseId = `sina-help-${action.id}`;
                return (
                  <div className="sina-action" key={action.id}>
                    <button
                      type="button"
                      onClick={() => setActiveActionId(isExpanded ? null : action.id)}
                      aria-expanded={isExpanded}
                      aria-controls={responseId}
                    >
                      <span>{action.label}</span>
                      <ChevronRight size={16} aria-hidden="true" />
                    </button>
                    {isExpanded ? (
                      <div id={responseId} className="sina-response" role="status">
                        <p>{action.response.intro}</p>
                        <ol>
                          {action.response.steps.map((step) => <li key={step}>{step}</li>)}
                        </ol>
                        <p className="sina-response__tip"><strong>Consejo:</strong> {action.response.tip}</p>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
            </> : null}
          </div>
        </section>
      ) : null}

      <button
        type="button"
        className={`sina-trigger ${gesture ? `sina-trigger--${gesture}` : ''} ${speakingState ? 'sina-trigger--talking' : ''}`}
        onClick={togglePanel}
        aria-label={isOpen ? 'Cerrar asistencia de Sina' : urgentAlerts.length ? `Abrir asistencia de Sina: ${urgentLabel}` : 'Abrir asistencia de Sina'}
        aria-expanded={isOpen}
        title={urgentAlerts.length ? `Sina: ${urgentLabel}` : 'Sina, asistencia operativa'}
      >
        {showTruck && !speakingState ? <SinaTruckLoader label="Sina está trabajando" leaving={truckLeaving} /> : <img src={getSinaState(speakingState || (urgentAlerts.length ? 'warning' : 'idle')).image} alt="" />}
        {speakingState || urgentAlerts.length || showTruck ? null : <>
          <img className="sina-trigger__frame sina-trigger__frame--look-left" src={SINA_IDLE_FRAMES.lookLeft} alt="" />
          <img className="sina-trigger__frame sina-trigger__frame--look-right" src={SINA_IDLE_FRAMES.lookRight} alt="" />
          <img className={`sina-trigger__frame sina-trigger__frame--blink ${blinking ? 'is-visible' : ''}`} src={SINA_IDLE_FRAMES.blink} alt="" />
        </>}
        <span className="sina-trigger__label">Sina</span>
        {urgentAlerts.length && !isOpen ? <span className="sina-trigger__badge" aria-hidden="true">{urgentAlerts.length}</span> : null}
      </button>
    </div>
  );
}
